/**
 * The language you read in, and where that choice is kept.
 *
 * A translated library holds the same work twice, and the two look alike on a
 * shelf: same author, same cover, titles that differ by a word. Left to itself
 * the grid offers both and lets you find out which one you picked by listening
 * to it. So the grid asks the server for works rather than editions, and this
 * is where it gets the language to ask for.
 *
 * The preference is remembered because it is not a filter: a filter is a
 * question you ask once, this is an answer that stays true. It is set from the
 * browser the first time, and afterwards by crossing to another edition from
 * inside a book, which is the moment a reader actually states a preference.
 */

const KEY = "readcommons:reading-lang";
const HOUSE = "readcommons:house-lang";
const CHANGED = "readcommons:reading-lang-changed";

/**
 * The language editions are chosen in.
 *
 * `"auto"` until a choice is made, and the server answers it with the language
 * the library is mostly in. Not the browser's: a phone's menus are set once, in
 * whatever the shop had, while a shelf full of translations was built on
 * purpose, book by book, by the person about to read it. That is the better
 * evidence of the two, and when it is wrong the header fixes it in one tap.
 *
 * Never empty: an empty preference turns the grid back into a list of editions,
 * which is the thing this exists to prevent.
 */
export function readingLang(): string {
  try {
    const stored = localStorage.getItem(KEY);
    if (stored && /^[a-z]{2}$/.test(stored)) return stored;
  } catch {
    // Private browsing denies storage. The library's own language still works.
  }
  return "auto";
}

export function setReadingLang(code: string): void {
  if (!/^[a-z]{2}$/.test(code) || code === readingLang()) return;
  try {
    localStorage.setItem(KEY, code);
  } catch {
    // Not being able to remember it is survivable; acting on it now is not.
  }
  window.dispatchEvent(new Event(CHANGED));
}

/** Subscribe to changes, including those made in another tab. */
export function onReadingLangChange(listener: () => void): () => void {
  const storage = (event: StorageEvent) => {
    if (event.key === KEY) listener();
  };
  window.addEventListener(CHANGED, listener);
  window.addEventListener("storage", storage);
  return () => {
    window.removeEventListener(CHANGED, listener);
    window.removeEventListener("storage", storage);
  };
}


/**
 * Remember the language the server said it resolved `auto` to.
 *
 * Kept because the interface needs it before the library has loaded, and on the
 * sign-in screen it will never load at all. Without this, a first paint has
 * nothing to go on but the browser, and the shelf then changes language under
 * the reader a second later, which looks like a bug and is one.
 */
export function rememberHouseLang(code: string): void {
  if (!/^[a-z]{2}$/.test(code)) return;
  try {
    if (localStorage.getItem(HOUSE) === code) return;
    localStorage.setItem(HOUSE, code);
  } catch {
    return; // Private browsing. The browser's own language still answers.
  }
  window.dispatchEvent(new Event(CHANGED));
}

/**
 * The language to fall back on when nothing has been chosen by hand.
 *
 * Empty until a library has been loaded once, which is the honest answer: the
 * caller has a browser setting to fall back on, and this has nothing better to
 * offer yet.
 */
export function uiLangFallback(): string {
  try {
    return localStorage.getItem(HOUSE) ?? "";
  } catch {
    return "";
  }
}
